/*
 * <page-loading-indicator> — 페이지 이동이 길어질 때 화면을 덮는 전역 로딩 스피너.
 * 마크업은 snippets/page-loading-indicator.liquid, 모양은 assets/component-page-loading.css.
 *
 * 이 테마는 SPA 가 아니라 링크 하나하나가 진짜 문서 이동입니다. 클릭과 새 문서의 첫 페인트
 * 사이에는 브라우저 탭의 작은 스피너 말고 아무 피드백이 없어서, 응답이 느린 이동(마켓/언어 전환,
 * KiTbetter 로그인·로그아웃 같은 크로스 도메인 왕복, 체크아웃)에서는 눌리지 않은 것처럼 보여
 * 같은 버튼을 두 번 누르게 됩니다. 그 빈 구간을 덮는 것이 이 컴포넌트입니다.
 *
 * 설계에서 지켜야 하는 것 세 가지:
 *
 *  1. 늦게 뜬다. SHOW_DELAY 안에 끝나는 이동에는 아무것도 그리지 않습니다 — 대부분의 이동이 그
 *     안에 끝나므로 지연 없이 띄우면 모든 클릭이 한 번 번쩍이는 것으로 끝납니다. 이 지연은
 *     타이머여야 하고 opacity 로 감추는 방식이면 안 됩니다: showModal() 은 부르는 즉시 페이지를
 *     inert 로 만들기 때문에, 안 보이는 채로 미리 열어 두면 취소된 이동이 화면을 잠급니다.
 *
 *  2. 이동을 가로채지 않는다. click/submit 을 window 의 버블 단계에서 듣고 defaultPrevented 만
 *     봅니다. document 에 붙은 다른 핸들러(script-modal-gate.js 의 성인 인증 게이트,
 *     script-product-form.js 의 AJAX 담기, script-facets.js 의 필터·정렬…)가 preventDefault 했다면
 *     문서 이동이 아니므로 그냥 넘어갑니다. window 는 버블의 마지막 정거장이라, 스크립트가 어떤
 *     순서로 로드되든 그 핸들러들 뒤에 실행되는 것이 보장됩니다 — document 에 같이 붙였다면
 *     등록 순서에 따라 preventDefault 를 못 보고 지나칠 수 있습니다.
 *
 *  3. 반드시 걷힌다. 이동이 일어나지 않는 경우(첨부파일로 응답해서 문서가 그대로인 클릭, 서버
 *     무응답)에 화면이 잠긴 채 남지 않도록 SAFETY_TIMEOUT 이 스스로 닫고, Escape 도 탈출구로
 *     열어 둡니다(<dialog> 의 기본 동작이라 막지 않는 것만으로 됩니다).
 *
 * form.submit() 이나 window.location.href 처럼 JS 가 직접 일으키는 이동은 click/submit 이벤트를
 * 내지 않으므로 여기서 잡을 수 없습니다. 그런 자리에서는 API 를 직접 부르세요:
 *
 *     window.pageLoading.start();              // SHOW_DELAY 뒤부터 스피너
 *     window.pageLoading.start({ delay: 0 });  // 즉시
 *     window.pageLoading.stop();               // 이동이 취소·실패했을 때
 */
if (!customElements.get('page-loading-indicator')) {
  /* 여기까지 걸린 이동만 기다릴 만한 이동으로 본다. 로컬 캐시나 가까운 CDN 히트는 대개 이 아래로
     끝나므로 스피너를 아예 보지 않게 된다. */
  const SHOW_DELAY = 400;

  /* 스피너가 혼자 남을 수 있는 최대 시간. 이동이 끝나면 문서가 사라지면서 같이 사라지므로, 이
     타이머가 실제로 터지는 건 이동이 일어나지 않았을 때뿐이다. */
  const SAFETY_TIMEOUT = 12000;

  class PageLoadingIndicator extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;

      this.showTimer = null;
      this.safetyTimer = null;

      // Escape·다이얼로그 자신의 close 등 어떤 경로로 닫혀도 안전 타이머는 여기서 정리한다.
      this.dialog.addEventListener('close', () => this.clearTimers());
    }

    disconnectedCallback() {
      this.clearTimers();
    }

    /** 이동이 시작됐다. `delay` 를 0 으로 주면 기다리지 않고 바로 띄운다. */
    start({ delay = SHOW_DELAY } = {}) {
      if (!this.dialog || this.dialog.open || this.showTimer) return;

      if (delay <= 0) {
        this.show();
        return;
      }
      this.showTimer = window.setTimeout(() => this.show(), delay);
    }

    /** 이동이 끝났거나 취소됐다. 아직 뜨지 않은 예약도 함께 취소한다. */
    stop() {
      if (!this.dialog) return;
      this.clearTimers();
      if (this.dialog.open) this.dialog.close();
    }

    show() {
      this.showTimer = null;
      if (this.dialog.open) return;

      try {
        this.dialog.showModal();
      } catch (error) {
        // 다이얼로그가 문서에서 떨어져 있으면 showModal() 이 던진다. 스피너 하나 못 띄우는 것이
        // 이동을 막을 이유는 없으므로 조용히 넘어간다.
        return;
      }

      this.safetyTimer = window.setTimeout(() => this.stop(), SAFETY_TIMEOUT);
    }

    clearTimers() {
      window.clearTimeout(this.showTimer);
      window.clearTimeout(this.safetyTimer);
      this.showTimer = null;
      this.safetyTimer = null;
    }
  }

  customElements.define('page-loading-indicator', PageLoadingIndicator);

  /* 엘리먼트가 아직 없을 수도 있는 상황(레이아웃에서 스니펫을 빼거나, 스크립트만 먼저 실행되는
     경우)에도 호출하는 쪽이 존재 여부를 확인하지 않아도 되도록 매번 찾아서 넘긴다. */
  const indicator = () => document.querySelector('page-loading-indicator');

  window.pageLoading = {
    start(options) {
      indicator()?.start(options);
    },
    stop() {
      indicator()?.stop();
    },
  };

  /* 이 클릭이 정말 이 문서를 떠나게 하는지 판정한다. 아니라고 판정할 이유가 하나라도 있으면
     띄우지 않는다 — 안 떠서 아쉬운 것보다 이동도 안 하는데 화면이 잠기는 게 훨씬 나쁘다. */
  const leavesDocument = (event) => {
    // 다른 핸들러가 이미 자기 일로 가져간 클릭(AJAX 담기, 성인 인증 게이트, 필터…).
    if (event.defaultPrevented) return false;
    // 새 탭/새 창으로 열리는 클릭은 이 문서를 떠나지 않는다.
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;

    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!link) return false;
    if (link.hasAttribute('download')) return false;
    if (link.target && link.target !== '_self') return false;
    // mailto:, tel:, javascript: — 문서는 그대로 남는다.
    if (link.protocol !== 'http:' && link.protocol !== 'https:') return false;

    // 같은 문서 안 앵커 이동. `#` 하나만 있는 href(드로어·아코디언 토글에 흔하다)도 여기서
    // 걸러야 하는데, 그건 URL 로는 프래그먼트 없는 현재 주소와 구별되지 않으므로 원본 href 에
    // '#' 이 있었는지를 함께 본다. 반대로 프래그먼트 없이 현재 주소를 다시 가리키는 링크는
    // 진짜 새로고침이므로 통과시킨다.
    const withoutHash = (url) => url.split('#')[0];
    if (
      (link.getAttribute('href') || '').includes('#') &&
      withoutHash(link.href) === withoutHash(window.location.href)
    ) {
      return false;
    }

    return true;
  };

  window.addEventListener('click', (event) => {
    if (leavesDocument(event)) window.pageLoading.start();
  });

  window.addEventListener('submit', (event) => {
    if (event.defaultPrevented) return;
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.target && form.target !== '_self') return;
    window.pageLoading.start();
  });

  /* bfcache 에서 돌아온 문서는 떠날 때의 DOM 을 그대로 들고 오므로, 스피너가 열린 채 저장됐다면
     뒤로 가기 화면이 스피너로 덮인 채 나타난다. 나가는 쪽(pagehide)과 돌아오는 쪽(pageshow)
     양쪽에서 닫는다 — pagehide 시점에는 새 문서가 이미 그려질 준비를 마쳤으므로 여기서 닫아도
     화면이 깜빡이지 않는다. */
  window.addEventListener('pagehide', () => window.pageLoading.stop());
  window.addEventListener('pageshow', () => window.pageLoading.stop());
}
